/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Host for the full profile MODAL (Figma 4136:14975), opened from the popover
// name-tap. Reads the chat-room shell VM (`userProfileUserId`), loads the peer
// profile, and renders `ProfileModalContent` in a centered, self-contained
// 511px panel (its own surface + padding, so it does NOT use the generic
// padded `Modal`). Dismisses on scrim-tap / hardware back.

import type React from 'react';
import { useEffect, useState } from 'react';
import { Modal as RNModal, Pressable, View } from 'react-native';

import type { ChatRoomShellViewModel } from '../chat/useChatRoomShellViewModel';
import { ProfileModalContent } from './ProfileModal';
import type { ProfileMoreMenuProps } from './ProfileMoreMenu';
import type { ProfileActionsFactory, ProfileView, ProfileViewLoader } from './profileViewTypes';

export interface ProfileModalOverlayProps {
  readonly vm: ChatRoomShellViewModel;
  readonly load: ProfileViewLoader;
  readonly buildActions?: ProfileActionsFactory | undefined;
  /** Builds the ⋯ "More" menu (groups + actions) for a given user. */
  readonly buildMoreMenu?: ((userId: string) => ProfileMoreMenuProps | undefined) | undefined;
}

// Soft elevation on the panel (react-native-web maps shadow* → boxShadow).
const PANEL_SHADOW = {
  shadowColor: '#000000',
  shadowOpacity: 0.3,
  shadowRadius: 24,
  shadowOffset: { width: 0, height: 12 },
  elevation: 12,
} as const;

export const ProfileModalOverlay: React.FC<ProfileModalOverlayProps> = ({
  vm,
  load,
  buildActions,
  buildMoreMenu,
}) => {
  const userId = vm.state.userProfileUserId;
  const visible = userId !== null;
  const close = (): void => vm.handlers.closeOverlay('userProfile');

  const [view, setView] = useState<ProfileView | null>(null);

  useEffect(() => {
    if (userId === null) {
      setView(null);
      return;
    }
    let alive = true;
    setView(null);
    void load(userId)
      .then((v) => {
        if (alive) setView(v);
      })
      .catch(() => {
        if (alive) setView(null);
      });
    return () => {
      alive = false;
    };
  }, [userId, load]);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={close}
      statusBarTranslucent
      accessibilityViewIsModal
      accessibilityLabel="User profile"
    >
      <Pressable
        className="flex-1 items-center justify-center bg-black/50 p-4"
        accessibilityRole="button"
        accessibilityLabel="Dismiss profile"
        onPress={close}
      >
        <Pressable
          onPress={(e) => e.stopPropagation()}
          style={PANEL_SHADOW}
          className="w-full max-w-[511px] max-h-[85%] overflow-hidden rounded-2xl border border-light-border-subtle dark:border-dark-border-subtle"
        >
          {view !== null && userId !== null ? (
            <ProfileModalContent
              view={view}
              actions={buildActions?.(userId) ?? {}}
              moreMenu={buildMoreMenu?.(userId)}
            />
          ) : (
            <View className="bg-light-surface-panel dark:bg-dark-surface-panel px-xl pt-xl pb-lg">
              <View className="flex flex-row items-center gap-md pb-lg border-b border-light-border-subtle dark:border-dark-border-subtle">
                <View className="w-14 h-14 rounded-full bg-light-surface-elevated dark:bg-dark-surface-elevated" />
                <View className="flex flex-col gap-[8px] flex-1">
                  <View className="h-[14px] rounded bg-light-surface-elevated dark:bg-dark-surface-elevated" style={{ width: "40%" }} />
                  <View className="h-[12px] rounded bg-light-surface-elevated dark:bg-dark-surface-elevated" style={{ width: "30%" }} />
                </View>
              </View>
              <View className="flex flex-row gap-sm pt-lg">
                <View className="flex-1 h-10 rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated" />
                <View className="flex-1 h-10 rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated" />
                <View className="flex-1 h-10 rounded-lg bg-light-surface-elevated dark:bg-dark-surface-elevated" />
              </View>
            </View>
          )}
        </Pressable>
      </Pressable>
    </RNModal>
  );
};
